import React from 'react';
import { Link } from 'react-router-dom';

import type { TextLinkProps } from './TextLink.types';

/** The inline link: sakura text, brighter on hover, a ring on keyboard focus. */
const LINK_CLASSES =
  'rounded-xs text-primary-300 transition-colors duration-fast hover:text-primary-200 hover:underline focus:outline-none focus-visible:ring-state-focus';

/** An action keeps the link's look, with a button's pointer and disabled state. */
const ACTION_CLASSES =
  'cursor-pointer disabled:cursor-not-allowed disabled:opacity-40';

/**
 * TextLink - an inline link inside running copy.
 *
 * The one place sakura appears in body text: a caption that names the card it
 * links to, a footnote pointing at another section, an action in a card's
 * copy ("Verified it already? Check again"). Pass `href` for an in-page anchor
 * or external URL, `to` for a router destination — never both — or neither
 * and an `onClick` for an action, which renders a button.
 *
 * @example
 * // An in-page anchor from a KPI caption
 * <TextLink href="#lifts">2 lifts stalled — see which</TextLink>
 *
 * // A router destination
 * <TextLink to="/goals">All goals</TextLink>
 *
 * // Leaving the app, in a new tab
 * <TextLink href="https://doi.org/10.1136/bjsports-2017-097608" external>
 *   Morton et al. 2018
 * </TextLink>
 *
 * // An action, held while it runs
 * <TextLink onClick={checkAgain} disabled={checking}>
 *   {checking ? 'Checking…' : 'Check again'}
 * </TextLink>
 *
 * // After a muted lead, inside the caller's line
 * <TextLink lead="Already have an account?" to="/login">Log in</TextLink>
 */
export function TextLink(textLinkProps: TextLinkProps): React.JSX.Element {
  const { lead } = textLinkProps;
  if (lead === undefined) return <LinkOnly {...textLinkProps} />;

  // Inline, so the caller's own line sets the size, alignment and spacing.
  return (
    <>
      <span className="text-neutral-500">{lead}</span>{' '}
      <LinkOnly {...textLinkProps} />
    </>
  );
}

function LinkOnly(textLinkProps: TextLinkProps): React.JSX.Element {
  if (textLinkProps.href === undefined && textLinkProps.to === undefined) {
    const {
      onClick,
      disabled = false,
      children,
      className = '',
    } = textLinkProps;
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`${LINK_CLASSES} ${ACTION_CLASSES} ${className}`.trim()}
        aria-label={textLinkProps['aria-label']}
        data-testid={textLinkProps['data-testid']}
      >
        {children}
      </button>
    );
  }

  const {
    href,
    to,
    external = false,
    children,
    className = '',
    lead: _lead,
    ...props
  } = textLinkProps;
  const classes = `${LINK_CLASSES} ${className}`.trim();

  if (to !== undefined) {
    return (
      <Link to={to} className={classes} {...props}>
        {children}
      </Link>
    );
  }

  return (
    <a
      href={href}
      className={classes}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      {...props}
    >
      {children}
    </a>
  );
}

export default TextLink;
